import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip, 
  Avatar,
  Collapse,
  Button,
  IconButton,
  Divider,
  Paper,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert
} from '@mui/material';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';
import NotesIcon from '@mui/icons-material/Notes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';

// バックエンドと統一した定数・パターン
const MAX_PHONE_LENGTH = 20;
const MAX_EMAIL_LENGTH = 255;
const MAX_ADDRESS_LENGTH = 100;
const PHONE_PATTERN = /^[0-9+()\\-\\s]+$/;
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const FORBIDDEN_CHARS = /[<>"'`;\\\/*?&|^$()[\]{}:\x00-\x1F\x7F]/;

// 郵便番号パターン（ハイフンあり・なし両対応）
const ZIPCODE_PATTERN = /^(\d{3}-?\d{4})$/;

/**
 * 顧客の基本情報を表示するカードコンポーネント（郵便番号検索対応版）
 */
const ClientInfoCard = ({ client, onEditNote, onContactUpdate }) => {
  const [noteExpanded, setNoteExpanded] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactData, setContactData] = useState({
    zipcode: '',
    address: '',
    phone: '',
    email: ''
  });
  const [contactErrors, setContactErrors] = useState({
    zipcode: '',
    address: '',
    phone: '',
    email: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [addressSearchResult, setAddressSearchResult] = useState('');

  // ローディング中の表示
  if (!client) {
    return (
      <Card elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ bgcolor: 'primary.main', p: 2, color: 'white' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Skeleton variant="circular" width={64} height={64} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
            <Box>
              <Skeleton variant="text" width={200} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
            </Box>
          </Box>
        </Box>
        <CardContent sx={{ pt: 3 }}>
          <Skeleton variant="rectangular" height={200} />
        </CardContent>
      </Card>
    );
  }

  const handleToggleNote = () => {
    setNoteExpanded(!noteExpanded);
  };

  // 郵便番号による住所検索
  const searchAddressByZipcode = async (zipcode) => {
    const normalizedZipcode = zipcode.replace(/-/g, '');
    
    if (!ZIPCODE_PATTERN.test(zipcode)) {
      setContactErrors(prev => ({ 
        ...prev, 
        zipcode: "郵便番号は7桁で入力してください（例：123-4567）" 
      }));
      return;
    }

    setIsSearchingAddress(true);
    setAddressSearchResult('');
    setContactErrors(prev => ({ ...prev, zipcode: '' }));

    try {
      const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${normalizedZipcode}`);
      const data = await response.json();

      if (data.status === 200 && data.results && data.results.length > 0) {
        const result = data.results[0];
        const fullAddress = `${result.address1}${result.address2}${result.address3}`;
        
        // UX改善：既存住所がある場合は確認メッセージ
        if (contactData.address.trim()) {
          const shouldReplace = window.confirm(
            `現在の住所：${contactData.address}\n\n` +
            `検索結果：${fullAddress}\n\n` +
            '住所を置き換えますか？（番地・建物名は手動で追加してください）'
          );
          
          if (!shouldReplace) {
            setIsSearchingAddress(false);
            return;
          }
        }
        
        setContactData(prev => ({ ...prev, address: fullAddress }));
        setAddressSearchResult(`✓ 住所が見つかりました: ${fullAddress}`);
        setContactErrors(prev => ({ ...prev, address: '' }));
      } else {
        setContactErrors(prev => ({ 
          ...prev, 
          zipcode: "該当する住所が見つかりませんでした" 
        }));
      }
    } catch (error) {
      setContactErrors(prev => ({ 
        ...prev, 
        zipcode: "住所検索でエラーが発生しました。しばらく経ってから再試行してください。" 
      }));
    } finally {
      setIsSearchingAddress(false);
    }
  };

  // 郵便番号入力時の自動検索
  const handleZipcodeChange = (value) => {
    setContactData(prev => ({ ...prev, zipcode: value }));
    setAddressSearchResult('');
    
    // 7桁入力完了時に自動検索
    const normalizedValue = value.replace(/-/g, '');
    if (normalizedValue.length === 7 && /^\d{7}$/.test(normalizedValue)) {
      searchAddressByZipcode(value);
    } else if (value.trim() === '') {
      setContactErrors(prev => ({ ...prev, zipcode: '' }));
    }
  };

  // 連絡先編集ダイアログを開く
  const handleOpenContactDialog = () => {
    setContactData({
      zipcode: '', // 郵便番号は空から開始（検索用）
      address: client?.address || '',
      phone: client?.phone || '',
      email: client?.email || ''
    });
    setContactErrors({ zipcode: '', address: '', phone: '', email: '' });
    setAddressSearchResult('');
    setContactDialogOpen(true);
  };

  // 連絡先編集ダイアログを閉じる
  const handleCloseContactDialog = () => {
    setContactDialogOpen(false);
    setContactData({ zipcode: '', address: '', phone: '', email: '' });
    setContactErrors({ zipcode: '', address: '', phone: '', email: '' });
    setAddressSearchResult('');
    setIsUpdating(false);
  };

  // 連絡先情報のバリデーション
  const validateContactField = (field, value) => {
    switch (field) {
      case 'address':
        if (!value || !value.trim()) return '';
        
        const address = value.trim();
        if (address.length > MAX_ADDRESS_LENGTH) {
          return '住所は100文字以内で入力してください';
        }
        if (FORBIDDEN_CHARS.test(address)) {
          return '住所に使用できない文字が含まれています';
        }
        return '';
        
      case 'phone':
        if (!value || !value.trim()) return '';
        
        const phone = value.trim();
        if (phone.length > MAX_PHONE_LENGTH) {
          return '電話番号は20文字以内で入力してください';
        }
        if (!PHONE_PATTERN.test(phone)) {
          return '電話番号に使用できない文字が含まれています';
        }
        
        const digitsOnly = phone.replace(/[^0-9]/g, '');
        if (digitsOnly.length > 0 && digitsOnly.length < 7) {
          return '電話番号の桁数が短すぎます';
        }
        return '';
        
      case 'email':
        if (!value || !value.trim()) return '';
        
        const email = value.trim();
        if (email.length > MAX_EMAIL_LENGTH) {
          return 'メールアドレスは255文字以内で入力してください';
        }
        
        if (/[<>"'`]/.test(email)) {
          return 'メールアドレスに使用できない文字が含まれています（< > " \' ` など）';
        }
        
        if (!EMAIL_PATTERN.test(email)) {
          return 'メールアドレスの形式が正しくありません（例：example@company.com）';
        }
        if (email.includes('..')) {
          return 'メールアドレスに連続したドットが含まれています';
        }
        
        const atCount = (email.match(/@/g) || []).length;
        if (atCount !== 1) {
          return 'メールアドレスの形式が正しくありません';
        }
        
        return '';
        
      default:
        return '';
    }
  };

  // 入力変更ハンドラー
  const handleContactInputChange = (field, value) => {
    if (field === 'zipcode') {
      handleZipcodeChange(value);
      return;
    }
    
    setContactData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'address') {
      setAddressSearchResult(''); // 手動編集時は検索結果をクリア
    }
    
    const error = validateContactField(field, value);
    setContactErrors(prev => ({ ...prev, [field]: error }));
  };

  // 連絡先情報を保存
  const handleSaveContact = async () => {
    // 最終バリデーション（郵便番号は保存対象外）
    const addressError = validateContactField('address', contactData.address);
    const phoneError = validateContactField('phone', contactData.phone);
    const emailError = validateContactField('email', contactData.email);
    
    if (addressError || phoneError || emailError) {
      setContactErrors(prev => ({ 
        ...prev, 
        address: addressError, 
        phone: phoneError, 
        email: emailError 
      }));
      return;
    }

    setIsUpdating(true);
    
    try {
      const token = sessionStorage.getItem('token');
      
      await axios.put(
        `/api/clients/${client.id}/contact`,
        {
          address: contactData.address.trim() || null,
          phone: contactData.phone.trim() || null,
          email: contactData.email.trim() || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      handleCloseContactDialog();
      
      if (onContactUpdate) {
        onContactUpdate();
      } else {
        window.location.reload();
      }
    } catch (error) {
      alert('連絡先情報の更新に失敗しました');
      setIsUpdating(false);
    }
  };

  // 備考の存在チェック
  const hasNote = Boolean(
    client?.note && 
    typeof client.note === 'string' && 
    client.note.trim().length > 0
  );
  
  // 備考の表示ロジック
  const getDisplayLogic = () => {
    if (!hasNote) {
      return { shouldTruncate: false, previewText: '', fullText: '' };
    }
    
    const noteText = client.note.trim();
    const lines = noteText.split('\n');
    const MAX_PREVIEW_LINES = 3;
    const MAX_PREVIEW_CHARS = 150;
    
    const shouldTruncateByLines = lines.length > MAX_PREVIEW_LINES;
    const shouldTruncateByChars = noteText.length > MAX_PREVIEW_CHARS;
    const shouldTruncate = shouldTruncateByLines || shouldTruncateByChars;
    
    let previewText = noteText;
    
    if (shouldTruncate) {
      if (shouldTruncateByLines) {
        previewText = lines.slice(0, MAX_PREVIEW_LINES).join('\n');
      } else {
        previewText = noteText.slice(0, MAX_PREVIEW_CHARS);
      }
    }
    
    return {
      shouldTruncate,
      previewText,
      fullText: noteText
    };
  };
  
  const { shouldTruncate, previewText, fullText } = getDisplayLogic();
  
  // 保存ボタンの有効/無効判定
  const isSaveDisabled = () => {
    return !!contactErrors.address || !!contactErrors.phone || !!contactErrors.email || isUpdating;
  };
  
  return (
    <>
      <Card elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ bgcolor: 'primary.main', p: 2, color: 'white' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar 
              sx={{ 
                width: 64, 
                height: 64, 
                bgcolor: 'white', 
                color: 'primary.main',
                fontWeight: 'bold',
                fontSize: 24
              }}
            >
              {client?.name ? client.name.charAt(0).toUpperCase() : '?'}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="medium" sx={{ wordBreak: "break-word" }}>
                {client?.name || '名前未設定'}
              </Typography>
              {client?.groupIds && Array.isArray(client.groupIds) && client.groupIds.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                  {client.groupIds.map(groupId => (
                    <Chip 
                      key={groupId} 
                      icon={<GroupIcon />} 
                      label={client.groupNames?.[groupId] || `グループ ${groupId}`}
                      size="small"
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        
        <CardContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            {/* 基本情報・連絡先 */}
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1" fontWeight="medium">
                  基本情報・連絡先
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={handleOpenContactDialog}
                  sx={{ 
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'primary.50' }
                  }}
                  title="基本情報・連絡先を編集"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
                <LocationOnIcon color="action" sx={{ mt: 0.5 }} />
                <Typography sx={{ wordBreak: "break-word" }}>
                  {client?.address || "住所未設定"}
                </Typography>
              </Box>
              
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <LocalPhoneIcon color="action" />
                <Typography>
                  {client?.phone || "電話番号未設定"}
                </Typography>
              </Box>
              
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <EmailIcon color="action" />
                <Typography sx={{ wordBreak: "break-all" }}>
                  {client?.email || "メールアドレス未設定"}
                </Typography>
              </Box>
            </Grid>

            {/* 備考欄 */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <NotesIcon color="action" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    備考
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      if (onEditNote) onEditNote();
                    }}
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'primary.50' }
                    }}
                    title="備考を編集"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  
                  {hasNote && shouldTruncate && (
                    <Button
                      size="small"
                      onClick={handleToggleNote}
                      endIcon={noteExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      {noteExpanded ? '閉じる' : 'もっと見る'}
                    </Button>
                  )}
                </Box>
              </Box>

              {hasNote ? (
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    position: 'relative'
                  }}
                >
                  {shouldTruncate ? (
                    <Collapse 
                      in={noteExpanded}
                      collapsedSize="4.8em"
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          lineHeight: 1.6
                        }}
                      >
                        {fullText}
                      </Typography>
                    </Collapse>
                  ) : (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: 1.6
                      }}
                    >
                      {fullText}
                    </Typography>
                  )}
                  
                  {shouldTruncate && !noteExpanded && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 15,
                        background: 'linear-gradient(transparent, #fafafa)',
                        pointerEvents: 'none'
                      }}
                    />
                  )}
                </Paper>
              ) : (
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    備考が登録されていません
                  </Typography>
                </Paper>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 郵便番号検索機能付き編集ダイアログ */}
      <Dialog 
        open={contactDialogOpen} 
        onClose={handleCloseContactDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOnIcon color="primary" />
          基本情報・連絡先の編集
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* 郵便番号検索 */}
            <TextField
              label="郵便番号"
              fullWidth
              value={contactData.zipcode}
              onChange={(e) => handleContactInputChange('zipcode', e.target.value)}
              error={!!contactErrors.zipcode}
              helperText={contactErrors.zipcode || '例：123-4567（自動で住所を検索します）'}
              placeholder="123-4567"
              size="small"
              InputProps={{
                endAdornment: isSearchingAddress ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => searchAddressByZipcode(contactData.zipcode)}
                      disabled={!contactData.zipcode.trim() || isSearchingAddress}
                      size="small"
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />

            {/* 検索結果表示 */}
            {addressSearchResult && (
              <Alert severity="success" sx={{ mb: 2, py: 1 }}>
                <Typography variant="body2">
                  {addressSearchResult}
                  <br />
                  <strong>ヒント：</strong>番地・建物名は下記で追加してください
                </Typography>
              </Alert>
            )}
            
            {/* 住所 */}
            <TextField
              label="住所"
              fullWidth
              value={contactData.address}
              onChange={(e) => handleContactInputChange('address', e.target.value)}
              error={!!contactErrors.address}
              helperText={contactErrors.address || '住所を入力してください（番地・建物名も含む）'}
              placeholder="住所を入力"
              size="small"
              InputProps={{
                startAdornment: <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
              }}
              sx={{ mb: 3 }}
            />
            
            {/* 電話番号 */}
            <TextField
              label="電話番号"
              fullWidth
              value={contactData.phone}
              onChange={(e) => handleContactInputChange('phone', e.target.value)}
              error={!!contactErrors.phone}
              helperText={contactErrors.phone || '例：090-1234-5678、03-1234-5678'}
              placeholder="電話番号を入力"
              size="small"
              InputProps={{
                startAdornment: <LocalPhoneIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
              }}
              sx={{ mb: 3 }}
            />
            
            {/* メールアドレス */}
            <TextField
              label="メールアドレス"
              fullWidth
              value={contactData.email}
              onChange={(e) => handleContactInputChange('email', e.target.value)}
              error={!!contactErrors.email}
              helperText={contactErrors.email || '例：example@company.com'}
              placeholder="メールアドレスを入力"
              type="email"
              size="small"
              InputProps={{
                startAdornment: <EmailIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseContactDialog}
            disabled={isUpdating}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveContact}
            disabled={isSaveDisabled()}
            sx={{ minWidth: 80 }}
          >
            {isUpdating ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientInfoCard;